import { supabase } from './supabase'
import type { HubLang } from './i18n'
import type { SignInLang } from './signInCopy'

/**
 * Emails and notifications (ACC-53, migration 326) and the Monday email
 * (CMP-11, 327).
 *
 * The server owns every answer: which categories exist and which settings
 * each takes come from my_communication_preferences(), which reads the code
 * catalogue (app.communication_categories). This file holds only the WORDS
 * for each key. A key the server sends that has no words here is shown by
 * its key rather than dropped, so a new category is visible, not lost.
 *
 * There is deliberately no person argument anywhere: the reads and writes are
 * the caller's own, and the unsubscribe is the token's.
 */

export type CommSetting = 'now' | 'weekly' | 'hub' | 'when_waiting' | 'off'

export type CommPreference = {
  category: string
  setting: CommSetting
  settings: CommSetting[]
  isDefault: boolean
  isDuty: boolean
}

type Row = { category: string; setting: string; settings: string[]; is_default: boolean; is_duty: boolean }

export async function loadMyCommunication(): Promise<CommPreference[]> {
  const { data, error } = await supabase.rpc('my_communication_preferences')
  if (error) throw error
  return ((data ?? []) as Row[]).map((r) => ({
    category: r.category,
    setting: r.setting as CommSetting,
    settings: r.settings as CommSetting[],
    isDefault: r.is_default,
    isDuty: r.is_duty,
  }))
}

/** Returns the setting now in force (a duty may answer 'now' while the Monday email is off). */
export async function setMyCommunication(category: string, setting: CommSetting): Promise<CommSetting> {
  const { data, error } = category === 'weekly_email'
    ? await supabase.rpc('set_my_weekly_email', { p_mode: setting })
    : await supabase.rpc('set_my_communication_preference', { p_category: category, p_setting: setting })
  if (error) throw error
  return data as CommSetting
}

export function isUnsubscribeToken(t: string): boolean {
  return /^[0-9a-f]{64}$/i.test(t)
}

/** The signed-out page's one call. Returns nothing about whose link it was. */
export async function unsubscribeByToken(token: string): Promise<'off'> {
  const { error } = await supabase.rpc('unsubscribe_by_token', { p_token: token })
  if (error) throw error
  return 'off'
}

/* ── words ──────────────────────────────────────────────────────────────── */

export type EmailsCopy = {
  head: string
  lede: string
  weeklyLabel: string
  weekly: Record<'weekly' | 'when_waiting' | 'off', string>
  weeklyHint: string
  categoriesHead: string
  setting: Record<'now' | 'weekly' | 'hub', string>
  category: Record<string, { label: string; hint: string }>
  dutyNote: string
  saved: string
  failed: string
  loadFailed: string
}

export const EMAILS: Record<HubLang, EmailsCopy> = {
  en: {
    head: 'Emails and notifications',
    lede: 'What OneConetec emails you, and how often. Everything also stays on the bell.',
    weeklyLabel: 'The Monday email',
    weekly: {
      weekly: 'Every Monday',
      when_waiting: "Only when something's waiting for me",
      off: 'Off',
    },
    weeklyHint: 'A short email at 7:00 on Monday, your time: what waits for you, your stars, One Question, the contests and news for your part of the company.',
    categoriesHead: 'For each kind of message',
    setting: { now: 'Email me at once', weekly: 'In the Monday email', hub: 'Only on the bell' },
    category: {
      recognitions: { label: 'Recognitions and stars', hint: 'Stars you received, and your team’s recognitions if you manage people.' },
      replies: { label: 'Replies to your posts', hint: 'Replies, replies to you, and mentions.' },
      support_replies: { label: 'Your private questions', hint: 'A reply, or your question moving to someone else.' },
      photo_tags: { label: 'Photos you are tagged in', hint: '' },
      orders: { label: 'Orders ready to collect', hint: '' },
      company: { label: 'One Question, contests and surveys', hint: '' },
      news: { label: 'News for your office or country', hint: 'Posts aimed at your part of the company, and Wiki One answers that apply to you.' },
      duties: { label: 'Work that comes with a role', hint: 'Moderation, support questions and photo reviews.' },
    },
    dutyNote: 'This comes with a role you hold, so it cannot be turned off. If the Monday email is off, it is emailed at once.',
    saved: 'Saved.',
    failed: 'That did not save. Try again.',
    loadFailed: 'Your email choices could not be loaded.',
  },
  es: {
    head: 'Correos y avisos',
    lede: 'Qué te envía OneConetec por correo y con qué frecuencia. Todo queda también en la campana.',
    weeklyLabel: 'El correo del lunes',
    weekly: {
      weekly: 'Todos los lunes',
      when_waiting: 'Solo cuando algo me espera',
      off: 'Desactivado',
    },
    weeklyHint: 'Un correo breve a las 7:00 del lunes, en tu hora: lo que te espera, tus estrellas, One Question, los concursos y noticias para tu parte de la empresa.',
    categoriesHead: 'Para cada tipo de mensaje',
    setting: { now: 'Enviarme un correo al momento', weekly: 'En el correo del lunes', hub: 'Solo en la campana' },
    category: {
      recognitions: { label: 'Reconocimientos y estrellas', hint: 'Las estrellas que recibiste, y los reconocimientos de tu equipo si tienes personas a cargo.' },
      replies: { label: 'Respuestas a tus publicaciones', hint: 'Respuestas, respuestas a ti y menciones.' },
      support_replies: { label: 'Tus preguntas privadas', hint: 'Una respuesta, o que tu pregunta pase a otra persona.' },
      photo_tags: { label: 'Fotos en que te etiquetaron', hint: '' },
      orders: { label: 'Pedidos listos para retirar', hint: '' },
      company: { label: 'One Question, concursos y encuestas', hint: '' },
      news: { label: 'Noticias para tu oficina o país', hint: 'Publicaciones dirigidas a tu parte de la empresa, y respuestas de Wiki One que te aplican.' },
      duties: { label: 'Trabajo que viene con un rol', hint: 'Moderación, preguntas de soporte y revisiones de fotos.' },
    },
    dutyNote: 'Viene con un rol que tienes, así que no se puede desactivar. Si el correo del lunes está desactivado, se envía al momento.',
    saved: 'Guardado.',
    failed: 'No se guardó. Inténtalo de nuevo.',
    loadFailed: 'No se pudieron cargar tus opciones de correo.',
  },
}

export type UnsubscribeCopy = {
  title: string
  lead: string
  button: string
  done: string
  doneNote: string
  bad: string
  failed: string
}

/** The signed-out page. One sentence, one button, nothing about whose link it is. */
export const UNSUBSCRIBE: Record<SignInLang, UnsubscribeCopy> = {
  en: {
    title: 'Stop the Monday email',
    lead: 'Press the button and OneConetec stops sending the Monday email to the address this link came to.',
    button: 'Stop the Monday email',
    done: 'Done. The Monday email will not come any more.',
    doneNote: 'Notices that come with a role you hold still arrive. You can turn the Monday email back on in your profile, under Emails and notifications.',
    bad: 'This link is not one we sent. Copy the whole link from the email and try again.',
    failed: 'That did not work. Try again in a minute.',
  },
  es: {
    title: 'Dejar de recibir el correo del lunes',
    lead: 'Presiona el botón y OneConetec deja de enviar el correo del lunes a la dirección a la que llegó este enlace.',
    button: 'Dejar de recibir el correo del lunes',
    done: 'Listo. El correo del lunes ya no llegará.',
    doneNote: 'Los avisos que vienen con un rol que tienes siguen llegando. Puedes volver a activar el correo del lunes en tu perfil, en Correos y avisos.',
    bad: 'Este enlace no es uno que enviamos. Copia el enlace completo del correo e inténtalo de nuevo.',
    failed: 'No funcionó. Inténtalo de nuevo en un minuto.',
  },
}
